import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import Wheel from "../components/clickwheel/Wheel";
import WheelList, { remaining } from "../components/clickwheel/WheelList";
import { LovedByDialog } from "../components/LovedByStack";
import { useShuffle } from "../components/NavAudioShuffle";
import api from "../lib/api";
import { playClick } from "../lib/profileSong";
import { useAuth } from "../context/AuthContext";

const MENU = [
  { key: "movies", label: "Movies" },
  { key: "tv", label: "TV Shows" },
  { key: "mylist", label: "My List" },
  { key: "continue", label: "Continue Watching" },
  { key: "search", label: "Search" },
];
const TITLES = { movies: "Movies", tv: "TV Shows", mylist: "My List", continue: "Continue Watching", loved: "Loved by Friends" };

function toRow(it, view) {
  const kind = it.media_type === "tv" ? "Series" : "Film";
  const n = it.friend_fav_count || 1;
  const sub = view === "continue" ? remaining(it)
    : view === "loved" ? `Loved by ${n} friend${n > 1 ? "s" : ""} · ${kind}`
    : [it.year, kind, it.rating ? `★ ${Number(it.rating).toFixed(1)}` : null].filter(Boolean).join(" · ");
  return { key: it.id, label: it.title, sub, img: it.poster || it.img, id: it.id, avatars: view === "loved" ? it.loved_by : undefined };
}

function useWheelData(user) {
  const [home, setHome] = useState(null);
  const [mylist, setMylist] = useState([]);
  const [loved, setLoved] = useState([]);
  useEffect(() => { api.get("/catalog/home").then(({ data }) => setHome(data)).catch(() => setHome({ rows: [] })); }, []);
  useEffect(() => { api.get("/catalog/loved-by-friends").then(({ data }) => setLoved(data.items || [])).catch(() => {}); }, []);
  const wl = (user?.watchlist || []).join(",");
  useEffect(() => { api.get("/watchlist").then(({ data }) => setMylist(data.items || [])).catch(() => {}); }, [wl]);
  return useMemo(() => {
    const rows = home?.rows || [];
    const seen = new Set();
    const all = rows.filter((r) => r.key !== "continue_watching").flatMap((r) => r.items).filter((i) => !seen.has(i.id) && seen.add(i.id));
    return {
      loading: !home,
      movies: all.filter((i) => i.media_type !== "tv"),
      tv: all.filter((i) => i.media_type === "tv"),
      mylist,
      loved,
      continue: rows.find((r) => r.key === "continue_watching")?.items || [],
    };
  }, [home, mylist, loved]);
}

export default function ClickWheelHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const shuffle = useShuffle();
  const data = useWheelData(user);
  const [view, setView] = useState(() => sessionStorage.getItem("cw_view") || "menu");
  const [sel, setSel] = useState({ menu: 0 });
  const [who, setWho] = useState(null);
  useEffect(() => { sessionStorage.setItem("cw_view", view); }, [view]);

  const menu = data.loved.length ? [...MENU.slice(0, 4), { key: "loved", label: "Loved by Friends" }, MENU[4]] : MENU;
  const rows = view === "menu"
    ? menu.map((m) => ({ ...m, img: m.key === "search" ? null : (data[m.key]?.[0]?.poster || data[m.key]?.[0]?.img), sub: m.key === "search" ? "Find any title" : `${data[m.key]?.length || 0} titles` }))
    : (data[view] || []).map((it) => toRow(it, view));
  const cur = Math.min(sel[view] || 0, Math.max(0, rows.length - 1));
  const setCur = (i) => setSel((s) => ({ ...s, [view]: i }));

  const step = (dir) => {
    if (!rows.length) return;
    const max = rows.length - 1;
    setSel((s) => {
      const from = Math.min(s[view] || 0, max);
      const next = Math.max(0, Math.min(max, from + dir));
      if (next !== from) playClick("tick");
      return next === from ? s : { ...s, [view]: next };
    });
  };
  const pick = (i) => {
    playClick("select");
    const r = rows[i];
    if (!r) return;
    if (view !== "menu") { navigate(`/title/${r.id}`); return; }
    if (r.key === "search") { navigate("/search"); return; }
    setCur(i); setView(r.key);
  };
  const back = () => { playClick("select"); setView("menu"); };
  const holdFn = view === "loved" ? (i) => { playClick("select"); const it = data.loved[i]; if (it) setWho(it); } : undefined;

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select, [contenteditable]") || document.querySelector("[role=dialog]")) return;
      if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
      else if (e.key === "Enter" && !e.target.closest?.("button, a")) pick(cur);
      else if (e.key === "Escape" || e.key === "Backspace") { if (view !== "menu") back(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const empty = view !== "menu" && !data.loading && rows.length === 0;
  const listRef = useRef(null);
  const dockRef = useRef(null);
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    // Desktop mouse wheel = wheel movement (non-passive so the page doesn't scroll instead).
    let acc = 0;
    const onWheel = (e) => {
      e.preventDefault();
      acc += e.deltaY;
      while (Math.abs(acc) >= 40) { const dir = acc > 0 ? 1 : -1; acc -= dir * 40; stepRef.current(dir); }
    };
    const els = [listRef.current, dockRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("wheel", onWheel, { passive: false }));
    return () => els.forEach((el) => el.removeEventListener("wheel", onWheel));
  }, [empty]);
  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-20 md:pb-0">
      <Navbar />
      <main className="mx-auto flex max-w-5xl flex-col px-3 pt-4 sm:px-6" data-testid="clickwheel-home">
        <section className="flex flex-col overflow-hidden rounded-[var(--card-radius)] border-2 border-[var(--text-primary)] bg-[var(--bg-card)]" style={{ height: "calc(100vh - 360px)", minHeight: 280 }} data-testid="clickwheel-screen">
          <header className="flex items-center gap-2 border-b-2 border-[var(--text-primary)] bg-[var(--bg-surface)] px-3 py-2">
            {view !== "menu" && <button type="button" onClick={back} className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--accent)]" data-testid="clickwheel-back"><ChevronLeft size={16} /> Menu</button>}
            <h1 className="flex-1 truncate text-center font-head text-base font-bold uppercase tracking-[0.2em] text-[var(--text-primary)]" data-testid="clickwheel-title">{view === "menu" ? "The Dial" : TITLES[view]}</h1>
            <span className="text-[11px] font-semibold text-[var(--text-muted)]" data-testid="clickwheel-position">{rows.length ? `${cur + 1} of ${rows.length}` : ""}</span>
          </header>
          <div ref={listRef} className="min-h-0 flex-1">
            {data.loading && view !== "menu" && <p className="p-4 text-sm text-[var(--text-secondary)]">Loading…</p>}
            {empty && <p className="p-6 text-center text-sm text-[var(--text-secondary)]" data-testid="clickwheel-empty">{view === "continue" ? "Nothing in progress yet — open a title and tap a service to start." : "Nothing here yet."}</p>}
            {!empty && <WheelList rows={rows} sel={cur} onHover={setCur} onPick={pick} testid={view === "menu" ? "clickwheel-menu" : "clickwheel-list"} menu={view === "menu"} onHold={holdFn} />}
          </div>
        </section>
        <div ref={dockRef} className="flex justify-center py-5" data-testid="clickwheel-dock">
          <Wheel onStep={step} onSelect={() => pick(cur)} onSelectHold={holdFn ? () => holdFn(cur) : undefined} onMenu={back} onShuffle={() => { playClick("select"); shuffle(); }} size={180} />
        </div>
      </main>
      <LovedByDialog item={who} open={!!who} onOpenChange={(o) => { if (!o) setWho(null); }} />
      <Disclaimer />
    </div>
  );
}
